import { useState } from "react";
import { ReadOnlyPill } from "@/components/auth/ReadOnlyPill";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Plus, Loader2 } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { useAudits } from "@/features/audits/api";
import { AUDIT_STATUSES } from "@/features/audits/types";
import { AuditStatusBadge } from "@/features/audits/badges";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { canCreateAudit } from "@/lib/roles";
import { useBranch } from "@/context/BranchContext";

export const Route = createFileRoute("/_authenticated/audits/")({
  component: AuditsIndex,
});

function AuditsIndex() {
  const navigate = useNavigate();
  const { data: me } = useCurrentUser();
  const [status, setStatus] = useState("all");
  const [mineOnly, setMineOnly] = useState(false);
  const { currentBranchId, lockedBranchId } = useBranch();
  const effectiveBranchId = lockedBranchId ?? currentBranchId ?? null;
  const { data: rows = [], isLoading } = useAudits({
    status: status === "all" ? null : status,
    mine: mineOnly,
    userId: me?.user.id ?? null,
    branchId: effectiveBranchId,
  });
  const canCreate = canCreateAudit(me?.roles ?? []);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Physical Asset Audits</h1> <ReadOnlyPill module="audits" />
          <p className="text-sm text-muted-foreground">Plan, execute and sign off physical verification cycles.</p>
        </div>
        {canCreate && (
          <Button onClick={() => navigate({ to: "/audits/new" })}><Plus className="mr-1 h-4 w-4" />New Audit</Button>
        )}
      </div>

      <Card><CardContent className="p-3">
        <div className="flex flex-wrap items-center gap-3">
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {AUDIT_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="flex items-center gap-2">
            <Switch id="mine" checked={mineOnly} onCheckedChange={setMineOnly} />
            <Label htmlFor="mine" className="text-sm">My audits only</Label>
          </div>
        </div>
      </CardContent></Card>

      <Card><CardContent className="p-0">
        {isLoading ? (
          <div className="grid place-items-center p-10"><Loader2 className="h-5 w-5 animate-spin" /></div>
        ) : rows.length === 0 ? (
          <div className="p-6 text-center text-sm text-muted-foreground">No audits found.</div>
        ) : (
          <Table>
            <TableHeader><TableRow>
              <TableHead>Name</TableHead><TableHead>Type</TableHead>
              <TableHead>Status</TableHead><TableHead>Start</TableHead><TableHead>End</TableHead>
              <TableHead>Auditors</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} className="cursor-pointer" onClick={() => navigate({ to: "/audits/$auditId", params: { auditId: r.id } })}>
                  <TableCell>
                    <Link to="/audits/$auditId" params={{ auditId: r.id }} className="text-primary hover:underline">{r.audit_name}</Link>
                  </TableCell>
                  <TableCell className="text-sm">{r.audit_type}</TableCell>
                  <TableCell><AuditStatusBadge status={r.status} /></TableCell>
                  <TableCell className="text-xs text-muted-foreground">{r.start_date ?? "—"}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{r.end_date ?? "—"}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{(r.assigned_auditors ?? []).length}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent></Card>
    </div>
  );
}
